import MagazineComponent, { MagazineContentVariant } from '@pixum/magazine';
import ThemeProvider, { DEFAULT_THEME, SUNFLOWER_THEME } from '@pixum/theme-provider';
import clsx from 'clsx';
import Text, { TEXT_TAG, TEXT_VARIANT } from '@pixum/text';
import { MAGAZINE_FOCUS } from 'data-layer/constants';
import type { Device } from 'data-layer';
import { MagazinePicture } from '../../magazineCollection/variants/partials/MagazinePicture';
import { RichTextBody, isRichTextEmpty } from '../../richtText';
import { NewsletterForm } from '../NewsletterForm';
import type { NewsletterMagazineProps } from '../types';
import { NewsletterSuccessMessageBox } from './NewsletterSuccessMessageBox';
import '../styles.css';

/**
 * Renders a newsletter registration section with the shared Magazine component.
 *
 * The component displays the newsletter headline, supporting content, image, and
 * registration form using the magazine layout and the configured newsletter theme.
 * When rendered as a key visual, the headline uses an h1 and the image is loaded
 * eagerly to support the page's largest contentful paint.
 *
 * @param props - Newsletter content, constants, theme, and optional magazine image.
 * @param className - Optional class name added to the magazine component.
 * @param articleTheme - Optional article theme that takes precedence over the magazine theme.
 * @param isUsedAsKeyVisual - Whether to render key visual heading and image loading behavior.
 */
export function NewsletterMagazine({
  props,
  className,
  articleTheme,
  isUsedAsKeyVisual = false
}: NewsletterMagazineProps) {
  const { magazine } = props;
  const { constants, headline, overline, text, textAfterMedia, image, pictureData, deviceType, theme } = magazine;
  const newsletterTheme = articleTheme || theme || SUNFLOWER_THEME;
  const content = (
    <div className="newsletter-magazine__content">
      {headline && (
        <Text
          variant={isUsedAsKeyVisual ? TEXT_VARIANT.TITLE_1 : TEXT_VARIANT.TITLE_2}
          tag={isUsedAsKeyVisual ? TEXT_TAG.H1 : TEXT_TAG.H2}
        >
          {headline}
        </Text>
      )}
      {overline && <Text variant={TEXT_VARIANT.BODY}>{overline}</Text>}
      {text && <RichTextBody text={text} variant={TEXT_VARIANT.BODY} wrapTextTag={TEXT_TAG.DIV} />}
      <NewsletterForm
        constants={constants}
        className="newsletter-magazine__form"
        theme={DEFAULT_THEME}
        successMessage={<NewsletterSuccessMessageBox constants={constants} theme={SUNFLOWER_THEME} />}
      />
      {!isRichTextEmpty(textAfterMedia) && (
        <RichTextBody text={textAfterMedia} variant={TEXT_VARIANT.FOOTNOTE} wrapTextTag={TEXT_TAG.DIV} />
      )}
    </div>
  );

  const media = image && (
    <MagazinePicture
      image={image}
      pictureData={pictureData}
      device={deviceType as Device}
      isUsedAsKeyVisual={isUsedAsKeyVisual}
    />
  );

  return (
    <ThemeProvider theme={newsletterTheme}>
      <MagazineComponent
        className={clsx('newsletter-magazine', className, articleTheme && 'newsletter-magazine--full-width')}
        focus={MAGAZINE_FOCUS.TEXT}
        level={isUsedAsKeyVisual ? 1 : 3}
        media={media}
        text={content}
        contentVariant={MagazineContentVariant.CONTENT_LEFT}
      />
    </ThemeProvider>
  );
}
